import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate, useParams } from 'react-router-dom';
import UserDashboard from 'layouts/UserDashboard';
import Container from 'components/Container';
import {
  getBusiness,
  listObjectives,
  createObjective,
  updateObjective,
  deleteObjective,
  updateBusiness,
} from './BusinessService';
import { getBusinessGoals } from '../Financial/FinancialService';
import {
  UserBusinessResponse,
  BusinessObjectiveResponse,
  GoalResponse,
} from '../Financial/types';

const objectiveStatusColor = (
  status: string,
): 'primary' | 'success' | 'default' => {
  if (status === 'ACTIVE') return 'primary';
  if (status === 'COMPLETED') return 'success';
  return 'default';
};

// ── Profile Edit Dialog ────────────────────────────────────────────────────────

interface ProfileEditDialogProps {
  open: boolean;
  business: UserBusinessResponse;
  onClose: () => void;
  onSaved: (updated: UserBusinessResponse) => void;
}

const ProfileEditDialog = ({
  open,
  business,
  onClose,
  onSaved,
}: ProfileEditDialogProps): JSX.Element => {
  const [name, setName] = useState(business.businessName);
  const [industry, setIndustry] = useState(business.industry ?? '');
  const [description, setDescription] = useState(business.description ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(business.businessName);
      setIndustry(business.industry ?? '');
      setDescription(business.description ?? '');
      setError(null);
    }
  }, [open, business]);

  function handleSave() {
    if (!name.trim()) return;
    setSaving(true);
    updateBusiness(business.id, {
      businessName: name.trim(),
      industry: industry || undefined,
      description: description || undefined,
    })
      .then((res) => {
        onSaved(res.data);
        onClose();
      })
      .catch(() => setError('Failed to save.'))
      .finally(() => setSaving(false));
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Business Profile</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          fullWidth
          size="small"
          label="Business Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
        />
        <TextField
          fullWidth
          size="small"
          label="Industry"
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          size="small"
          label="Description"
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!name.trim() || saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Objective Dialog ───────────────────────────────────────────────────────────

interface ObjectiveDialogProps {
  open: boolean;
  initial: BusinessObjectiveResponse | null;
  onClose: () => void;
  onSaved: (obj: BusinessObjectiveResponse) => void;
  businessId: number;
}

const ObjectiveDialog = ({
  open,
  initial,
  onClose,
  onSaved,
  businessId,
}: ObjectiveDialogProps): JSX.Element => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTitle(initial?.title ?? '');
      setDescription(initial?.description ?? '');
      setTargetDate(initial?.targetDate ?? '');
      setOwnerName(initial?.ownerName ?? '');
      setStatus(initial?.status ?? 'ACTIVE');
      setError(null);
    }
  }, [open, initial]);

  function handleSave() {
    if (!title.trim()) return;
    setSaving(true);
    const payload = {
      title: title.trim(),
      description: description || undefined,
      targetDate: targetDate || undefined,
      ownerName: ownerName || undefined,
      status: initial ? status : undefined,
    };

    const call = initial
      ? updateObjective(businessId, initial.id, payload)
      : createObjective(businessId, payload);

    call
      .then((res) => {
        onSaved(res.data);
        onClose();
      })
      .catch(() => setError('Failed to save objective.'))
      .finally(() => setSaving(false));
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initial ? 'Edit Objective' : 'Add Objective'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          fullWidth
          size="small"
          label="Title (required)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
        />
        <TextField
          fullWidth
          size="small"
          label="Description"
          multiline
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          size="small"
          label="Target Date"
          type="date"
          value={targetDate}
          onChange={(e) => setTargetDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          size="small"
          label="Owner Name"
          value={ownerName}
          onChange={(e) => setOwnerName(e.target.value)}
          sx={{ mb: initial ? 2 : 0 }}
        />
        {initial && (
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="ARCHIVED">Archived</MenuItem>
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!title.trim() || saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Delete Confirm Dialog ──────────────────────────────────────────────────────

interface DeleteDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DeleteConfirmDialog = ({
  open,
  onClose,
  onConfirm,
}: DeleteDialogProps): JSX.Element => (
  <Dialog open={open} onClose={onClose} maxWidth="xs">
    <DialogTitle>Delete this objective?</DialogTitle>
    <DialogContent>
      <Typography variant="body2">This action cannot be undone.</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="contained" color="error" onClick={onConfirm}>
        Delete
      </Button>
    </DialogActions>
  </Dialog>
);

// ── Main Page ──────────────────────────────────────────────────────────────────

const BusinessWorkspacePage = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const businessId = Number(id);
  const navigate = useNavigate();

  const [business, setBusiness] = useState<UserBusinessResponse | null>(null);
  const [objectives, setObjectives] = useState<BusinessObjectiveResponse[]>([]);
  const [linkedGoals, setLinkedGoals] = useState<GoalResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [profileEditOpen, setProfileEditOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editTarget, setEditTarget] =
    useState<BusinessObjectiveResponse | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<BusinessObjectiveResponse | null>(null);

  useEffect(() => {
    load();
  }, [businessId]);

  function load() {
    setLoading(true);
    Promise.all([
      getBusiness(businessId),
      listObjectives(businessId),
      getBusinessGoals(businessId),
    ])
      .then(([bRes, oRes, gRes]) => {
        setBusiness(bRes.data);
        setObjectives(oRes.data);
        setLinkedGoals(gRes.data);
      })
      .catch(() => setError('Failed to load business workspace.'))
      .finally(() => setLoading(false));
  }

  function handleObjectiveSaved(obj: BusinessObjectiveResponse) {
    setObjectives((prev) => {
      const idx = prev.findIndex((o) => o.id === obj.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = obj;
        return next;
      }
      return [...prev, obj];
    });
  }

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    deleteObjective(businessId, deleteTarget.id)
      .then(() => {
        setObjectives((prev) => prev.filter((o) => o.id !== deleteTarget.id));
        setDeleteTarget(null);
      })
      .catch(() => setError('Failed to delete objective.'));
  }

  if (loading) {
    return (
      <UserDashboard>
        <Container>
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        </Container>
      </UserDashboard>
    );
  }

  if (!business) {
    return (
      <UserDashboard>
        <Container>
          <Alert severity="error">Business not found.</Alert>
        </Container>
      </UserDashboard>
    );
  }

  const activeCount = objectives.filter((o) => o.status === 'ACTIVE').length;
  const completedCount = objectives.filter(
    (o) => o.status === 'COMPLETED',
  ).length;

  return (
    <UserDashboard>
      <Container>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* ── Header ── */}
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          mb={3}
        >
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant="h5" fontWeight={700}>
                {business.businessName}
              </Typography>
              <Tooltip title="Edit profile">
                <IconButton
                  size="small"
                  onClick={() => setProfileEditOpen(true)}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              {business.templateType && (
                <Chip
                  label={business.templateType.replace('_', ' ')}
                  size="small"
                  color="primary"
                />
              )}
              <Chip
                label={business.status}
                size="small"
                color={
                  business.status === 'ACTIVE'
                    ? 'success'
                    : business.status === 'ARCHIVED'
                      ? 'error'
                      : 'default'
                }
              />
            </Box>
          </Box>
          <Button variant="outlined" onClick={() => navigate('/user/business')}>
            All Businesses
          </Button>
        </Box>

        {/* ── Draft alert ── */}
        {business.status === 'DRAFT' && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Setup incomplete.{' '}
            <Link
              href={`/user/business/${businessId}/setup`}
              underline="always"
            >
              Complete the setup wizard
            </Link>{' '}
            to activate your business workspace.
          </Alert>
        )}

        {/* ── KPI row ── */}
        <Grid container spacing={2} mb={4}>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {activeCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Objectives
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {completedCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed Objectives
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Chip
                  label={business.templateType?.replace('_', ' ') ?? 'N/A'}
                  color="primary"
                  size="small"
                />
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Business Type
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Objectives ── */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h6" fontWeight={600}>
            Objectives
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditTarget(null);
              setAddDialogOpen(true);
            }}
          >
            Add Objective
          </Button>
        </Box>

        {objectives.length === 0 ? (
          <Typography variant="body2" color="text.secondary" py={4}>
            No objectives yet. Add your first objective to track business goals.
          </Typography>
        ) : (
          <Box>
            {objectives.map((obj, i) => (
              <React.Fragment key={obj.id}>
                {i > 0 && <Divider />}
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  py={1.5}
                >
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body1" fontWeight={500} noWrap>
                      {obj.title}
                    </Typography>
                    {obj.ownerName && (
                      <Typography variant="body2" color="text.secondary">
                        {obj.ownerName}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    ml={2}
                    flexShrink={0}
                  >
                    <Chip
                      label={obj.targetDate ?? 'No due date'}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={obj.status}
                      size="small"
                      color={objectiveStatusColor(obj.status)}
                    />
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditTarget(obj);
                          setAddDialogOpen(true);
                        }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => setDeleteTarget(obj)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </React.Fragment>
            ))}
          </Box>
        )}

        {/* ── Linked Goals ── */}
        <Box mt={4}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Linked Goals
          </Typography>
          {linkedGoals.length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={2}>
              No goals linked yet. Link a goal from the Goals page.
            </Typography>
          ) : (
            <Box>
              {linkedGoals.map((g, i) => (
                <React.Fragment key={g.id}>
                  {i > 0 && <Divider />}
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                    py={1.5}
                    flexWrap="wrap"
                  >
                    <Box flex={1} minWidth={140}>
                      <Typography variant="body2" fontWeight={600}>
                        {g.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        £{g.currentAmount.toLocaleString()} / £{g.targetAmount.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ width: 160 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(g.progressPercent, 100)}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ minWidth: 40, textAlign: 'right' }}
                    >
                      {Math.round(g.progressPercent)}%
                    </Typography>
                    <Chip
                      label={g.status.charAt(0) + g.status.slice(1).toLowerCase()}
                      size="small"
                      color={
                        g.status === 'COMPLETED'
                          ? 'success'
                          : g.status === 'ACTIVE'
                            ? 'primary'
                            : 'default'
                      }
                      sx={{ fontSize: '0.68rem', height: 22 }}
                    />
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          )}
        </Box>

        {/* ── Dialogs ── */}
        <ProfileEditDialog
          open={profileEditOpen}
          business={business}
          onClose={() => setProfileEditOpen(false)}
          onSaved={(updated) => setBusiness(updated)}
        />

        <ObjectiveDialog
          open={addDialogOpen}
          initial={editTarget}
          businessId={businessId}
          onClose={() => {
            setAddDialogOpen(false);
            setEditTarget(null);
          }}
          onSaved={handleObjectiveSaved}
        />

        <DeleteConfirmDialog
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
        />
      </Container>
    </UserDashboard>
  );
};

export default BusinessWorkspacePage;
